import { isBrowser } from 'constants/browser';
import clonedeep from 'lodash.clonedeep';
import { addNewWidget, setActiveWidget, store } from 'modules/Editor/store';
import { selectWidgets } from 'modules/Editor/store/infographSelector';
import { CSSProperties } from 'react';
import { ConnectedLineData, ConnectedWidgetData, PortState, WrapperPos } from 'types/flowCore.types';
import { WidgetId } from 'types/idTypes';
import { WidgetsMap } from 'types/infographTypes';
import { WidgetPosition, WidgetType } from 'types/widget.types';
import { getGroupTargetDataFromWidgetId, getGroupWidgetPositionFromWidgetIds } from 'utils/dom';
import { getRectCenterFromPointOnSide, rotatePointAroundOrigin } from 'utils/transform';
import { ENABLED_CENTER_WIDGET_TYPES } from 'widgets/Lines/LineWidget/LineWidget.config';
import { Alignment, LineWidgetData, Pos, Side } from 'widgets/Lines/LineWidget/LineWidget.types';
import { ResponsiveWidgetBaseData } from 'widgets/ResponsiveWidgets/ResponsiveWidgetBase.types';
import { RESPONSIVE_WIDGET_LIST } from 'widgets/Widget.config';
import { generateWidgetId, getRenderedEditableWidgetIds, getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData, GroupWidgetData, NewWidget, WidgetMap } from 'widgets/Widget.types';
import { isWidgetsSimilar } from 'widgets/WidgetComparisonStrategy';
import { lineConnectionCache } from 'widgets/sdk/LineConnectionCache';

import { getClipboardGroupWidgets, parseCopyWidgets } from '../Clipboard/Clipboard.helpers';
import {
  BUTTON_GRID_WIDGET_PADDING_PX,
  BUTTON_GRID_WIDGET_PX,
  FLOW_MODE_WIDGET_SELECTOR_CLASS,
} from './FlowCore.config';

/**
 * Calculates the maximum diameter of a circular port that can fit within a
 * specified rectangular area, while also considering a maximum size limit.
 *
 * @param width The width of the rectangular area.
 * @param height The height of the rectangular area.
 * @param maxSize The maximum allowable size (diameter) of the port.
 * @returns The maximum diameter of the port that can fit within the rectangular
 *          area, limited by maxSize.
 */
export const calculateMaxPortSize = (width: number, height: number, maxSize: number): number => {
  // Calculate the diagonal distance between the center points of adjacent circles
  const diagonalDistance = Math.sqrt(Math.pow(width / 2, 2) + Math.pow(height / 2, 2));

  // Return the diameter or maxSize, whichever is smaller
  return Math.min(diagonalDistance, maxSize);
};

/**
 * Generates a list of connected widget data with specific filtering and sorting criteria.
 *
 * @param {WidgetsMap} widgets - A map of widgets.
 * @param {ConnectedWidgetData[] | undefined} connectedWidgetsData - Array of data about widgets that are connected.
 * @param {WidgetId | undefined} currentNodeId - The ID of the current node/widget.
 * @param {WidgetId[]} widgetLayerOrder - An array representing the order of widgets based on their layer.
 * @returns {ConnectedWidgetData[]} A filtered and sorted array of connected widget data.
 *
 * The function performs the following operations:
 * 1. Returns an empty array if connectedWidgetsData is not provided.
 * 2. Filters out the widgets that are either the current node or not included in the widgetLayerOrder.
 * 3. Sorts the remaining widgets, prioritizing 'Group' widgets.
 * 4. Filters out widgets that are similar to those already in the list, based on a similarity criteria.
 * 5. Limits the result to the first 12 items in the list.
 */
export const generateAddNodeWidgets = (
  widgets: WidgetsMap,
  connectedWidgetsData: ConnectedWidgetData[] | undefined,
  currentNodeId: WidgetId | undefined,
  widgetLayerOrder: WidgetId[],
): ConnectedWidgetData[] => {
  if (!connectedWidgetsData) return [];

  const renderedConnectedWidgets = connectedWidgetsData
    .filter((data) => data.widgetId !== currentNodeId && widgetLayerOrder.includes(data.widgetId))
    .sort((a, b) => {
      // Sort so Group widgets are first
      const isAGroup = getWidgetTypeFromId(a.widgetId) === WidgetType.Group;
      const isBGroup = getWidgetTypeFromId(b.widgetId) === WidgetType.Group;
      return isAGroup === isBGroup ? 0 : isAGroup ? -1 : 1;
    });

  // Filter out similar widgets
  const filteredWidgets = [];
  for (let i = 0; i < renderedConnectedWidgets.length; i++) {
    let isSimilar = false;
    for (let j = 0; j < filteredWidgets.length; j++) {
      if (
        isWidgetsSimilar(
          [renderedConnectedWidgets[i].widgetId, widgets[renderedConnectedWidgets[i].widgetId]],
          [filteredWidgets[j].widgetId, widgets[filteredWidgets[j].widgetId]],
          widgets,
        )
      ) {
        isSimilar = true;
        break;
      }
    }

    if (!isSimilar) {
      filteredWidgets.push(renderedConnectedWidgets[i]);
    }
  }

  // Return only the first 12 items
  return filteredWidgets.slice(0, 12);
};

/**
 * Return the top and left value of the port renderer
 * @param wrapperPosition
 * @param side
 * @returns {CSSProperties}
 */
export const generateWrapperStyle = (
  wrapperPosition: WrapperPos,
  side: Side,
  zoom: number = 1,
  padding: number = 0,
  rotationInDegrees: number = 0,
  includeHandleOffset: boolean = false,
): CSSProperties => {
  let topPx = wrapperPosition.topPx;
  let leftPx = wrapperPosition.leftPx;
  let widthPx = wrapperPosition.widthPx;
  let heightPx = wrapperPosition.heightPx;

  // Center of the widget
  const centerX = leftPx + widthPx / 2;
  const centerY = topPx + heightPx / 2;

  switch (side) {
    case Side.NORTH:
      leftPx += widthPx / 2;
      topPx -= padding;
      break;
    case Side.EAST:
      leftPx += widthPx + padding;
      topPx += heightPx / 2;
      break;
    case Side.SOUTH:
      leftPx += widthPx / 2;
      topPx += heightPx + padding;
      break;
    case Side.WEST:
      leftPx -= padding;
      topPx += heightPx / 2;
      break;
    case Side.CENTER:
      leftPx += widthPx / 2;
      topPx += heightPx / 2;
      break;
  }
  const rotateAboutOrigin = { xPx: centerX * zoom, yPx: centerY * zoom };
  const rotatedPosition = rotatePointAroundOrigin(
    {
      xPx: leftPx * zoom,
      yPx: topPx * zoom,
    },
    rotateAboutOrigin,
    rotationInDegrees,
  );
  return {
    top: `${rotatedPosition.yPx}px`,
    left: `${rotatedPosition.xPx}px`,
  };
};

/**
 * Get rotation of the port icon based on the side
 * Port icon at rotation 0 points EAST
 *
 * @param side
 * @returns
 */
export const getPortIconRotation = (side: Side) => {
  switch (side) {
    case Side.NORTH:
      return 270;
    case Side.EAST:
      return 0;
    case Side.SOUTH:
      return 90;
    case Side.WEST:
      return 180;
    default:
      return 0;
  }
};

/**
 * Computes the offset of the LineCreator's padding style from the normal style.
 * @param wrapperPosition
 * @param side
 * @param zoom
 * @param padding
 * @returns
 */
export const getWrapperOffset = (
  wrapperPosition: WrapperPos,
  side: Side,
  zoom: number = 1,
  padding: number = 0,
  rotationInDegrees: number = 0,
): Pos => {
  const wrapperStyle = generateWrapperStyle(wrapperPosition, side, zoom, 0, rotationInDegrees);
  const wrapperWithPaddingStyle = generateWrapperStyle(wrapperPosition, side, zoom, padding, rotationInDegrees);
  return {
    xPx: parseFloat(wrapperWithPaddingStyle.left as string) - parseFloat(wrapperStyle.left as string),
    yPx: parseFloat(wrapperWithPaddingStyle.top as string) - parseFloat(wrapperStyle.top as string),
  };
};
/**
 * Return the portState data by side and widget Id
 * @param side
 * @returns {PortState}
 */
export const generatePortState = (side: Side, widgetId: WidgetId): PortState => ({
  widgetId,
  side: side,
  alignment: Alignment.CENTER,
});

/**
 * Returns a map with [widgetId]: [lineWidgetId, lineWidgetId, ...]
 * The widgetId is the node and the lineWidgetId are the line(s) that connects to the node
 *
 * @param widgets - the entire widgets map
 * @returns {ConnectedLineData}
 */
export const getConnectedLineData = (widgets: WidgetsMap): ConnectedLineData => {
  const renderedLineWidgetIds = getRenderedEditableWidgetIds(WidgetType.Line);
  const connectLineDataObj = renderedLineWidgetIds.reduce(
    (prev: ConnectedLineData, widgetId: WidgetId): ConnectedLineData => {
      const lineWidgetData = widgets[widgetId] as LineWidgetData;

      if (lineWidgetData?.startPort?.widgetId && typeof lineWidgetData?.startPort?.widgetId === 'string') {
        prev[lineWidgetData?.startPort?.widgetId]
          ? prev[lineWidgetData?.startPort?.widgetId].push(widgetId)
          : (prev[lineWidgetData?.startPort?.widgetId] = [widgetId]);
      }

      if (lineWidgetData?.endPort?.widgetId) {
        prev[lineWidgetData?.endPort?.widgetId]
          ? prev[lineWidgetData?.endPort?.widgetId].push(widgetId)
          : (prev[lineWidgetData?.endPort?.widgetId] = [widgetId]);
      }

      return prev;
    },
    {},
  );

  return connectLineDataObj;
};

/**
 * Return the object list that has widgetId and widgetData
 *
 * @param widgets
 * @param connectedLineData
 * @returns {ConnectedWidgetData[]}
 */
export const getConnectedWidgetData = (
  widgets: WidgetsMap,
  connectedLineData: ConnectedLineData,
): ConnectedWidgetData[] =>
  Object.keys(connectedLineData)
    .filter((widgetId) => widgets[widgetId])
    .map((widgetId) => ({ widgetId: widgetId, widgetData: widgets[widgetId] as AllWidgetData }))
    .filter((data) => data?.widgetData) ?? [];

/**
 * Calculate the widget scale value so that the widget can be displayed inside the button
 *
 * @param heightPx
 * @param widthPx
 * @returns
 */
export const getWidgetScale = (heightPx: number, widthPx: number): number => {
  const isSmallerThanTumbnail =
    heightPx <= BUTTON_GRID_WIDGET_PX - BUTTON_GRID_WIDGET_PADDING_PX * 2 &&
    widthPx <= BUTTON_GRID_WIDGET_PX - BUTTON_GRID_WIDGET_PADDING_PX * 2;
  if (isSmallerThanTumbnail) return 1;

  const hScale = BUTTON_GRID_WIDGET_PX / heightPx;
  const wScale = BUTTON_GRID_WIDGET_PX / widthPx;

  return Math.min(wScale, hScale);
};

/**
 * Retrieves the dimensions of a widget based on its connected widget data.
 *
 * @function
 * @param {ConnectedWidgetData} connectedData - The data of the connected widget.
 * @returns {{ heightPx: number; widthPx: number }} - An object containing the height and width of the widget in pixels.
 * @throws Will throw an error if the `getWidgetTypeFromId` or `getGroupTargetDataFromWidgetId` function calls fail.
 *
 * @example
 * const widgetDimensions = getWidgetDimensionsFromConnectedWidgetData(someConnectedData);
 * // => { heightPx: 200, widthPx: 100 } or { heightPx: 0, widthPx: 0 }
 */
export const getWidgetDimensionsFromConnectedWidgetData = (
  connectedData: ConnectedWidgetData,
): { heightPx: number; widthPx: number } => {
  if (getWidgetTypeFromId(connectedData.widgetId) === WidgetType.Group) {
    // get height and width from children using custom func
    const groupTargetData = getGroupTargetDataFromWidgetId(
      connectedData.widgetId,
      connectedData.widgetData as GroupWidgetData,
    );
    return !!groupTargetData
      ? { heightPx: groupTargetData.wrapperPos.heightPx, widthPx: groupTargetData.wrapperPos.widthPx }
      : { heightPx: 0, widthPx: 0 };
  } else {
    return {
      heightPx: connectedData.widgetData.heightPx,
      widthPx: connectedData.widgetData.widthPx,
    };
  }
};

/**
 * Calculate the button widget left and top style after scaling the widget
 *
 * @param heightPx
 * @param widthPx
 * @returns
 */
export const getButtonWidgetStyle = (heightPx: number, widthPx: number) => {
  const scale = getWidgetScale(heightPx, widthPx);
  const isSmallerThanTumbnail = scale === 1;

  // Initial value is -1 due to the border size
  let topPx = -1,
    leftPx = -1;

  if (isSmallerThanTumbnail) {
    leftPx = (BUTTON_GRID_WIDGET_PX - widthPx * scale) / 2;
    topPx = (BUTTON_GRID_WIDGET_PX - heightPx * scale) / 2;
  } else {
    if (heightPx > widthPx) {
      leftPx = (BUTTON_GRID_WIDGET_PX - widthPx * scale) / 2;
    } else if (heightPx < widthPx) {
      topPx = (BUTTON_GRID_WIDGET_PX - heightPx * scale) / 2;
    }
  }

  return {
    left: `${leftPx + BUTTON_GRID_WIDGET_PADDING_PX}px`,
    top: `${topPx + BUTTON_GRID_WIDGET_PADDING_PX}px`,
  };
};

/**
 * Return the widget selector for the flow mode
 *
 * @param widgetId
 * @returns
 */
export const getFlowModeWidgetSelector = (widgetId: string) => `${FLOW_MODE_WIDGET_SELECTOR_CLASS}[id="${widgetId}"]`;

/**
 * Get opposite side of the given side
 *
 * @param side
 * @returns opposite
 */
export const getOppositeSide = (side: Side): Side => {
  if (side === Side.EAST) {
    return Side.WEST;
  } else if (side === Side.WEST) {
    return Side.EAST;
  } else if (side === Side.NORTH) {
    return Side.SOUTH;
  } else if (side === Side.SOUTH) {
    return Side.NORTH;
  }
  return Side.CENTER;
};

/**
 * Checks if the currently active widget is connected to any other widget.
 * @return {boolean} - Returns true if the active widget is connected, otherwise returns false.
 * Note: Currently only supports the first widget in case of multi-selection.
 */
export const getIsConnectedActiveWidget = (activeWidgetIds: WidgetId[]) => {
  if (!activeWidgetIds || !activeWidgetIds.length) return false;
  const isConnected = activeWidgetIds.some((widgetId) => lineConnectionCache.isConnected(widgetId));

  return isConnected;
};

/**
 * Checks if any of the currently active line widgets are connected to any other widget.
 * @return {boolean} - Returns true if any of the line active widgets are connected, otherwise returns false.
 */
export const getIsActiveWidgetsHasConnectedLine = (activeWidgetIds: WidgetId[], widgetMap: WidgetMap) => {
  if (!activeWidgetIds || !activeWidgetIds.length || activeWidgetIds.length < 2) return false;

  return activeWidgetIds.some((widgetId) => {
    const widgetIsLine: boolean = getWidgetTypeFromId(widgetId) === WidgetType.Line;
    const widgetExists = document.getElementById(widgetId)!!;
    const widgetDataAsLine = widgetMap[widgetId] as LineWidgetData;

    const hasConnectedEnd =
      (!!widgetDataAsLine && 'endPort' in widgetDataAsLine && !!widgetDataAsLine.endPort?.widgetId) ||
      (!!widgetDataAsLine && 'startPort' in widgetDataAsLine && !!widgetDataAsLine?.startPort?.widgetId);

    return widgetIsLine && widgetExists && hasConnectedEnd;
  });
};

/**
 * Calculates the new position for a widget based on the position and properties of a line widget and the side of the widget to align with.
 *
 * @param newWidgetData Generic widget data structure for all widget types in the infographic editor.
 * @param newLineWidgetData Data for the line widget that is being created.
 * @param side The side of the rectangle of the widget (e.g., Side.NORTH, Side.EAST, etc.).
 * @returns An object of type WrapperPos representing the new top and left positions, width, and height of the widget.
 */
const calculateNewWidgetPos = (
  newWidgetData: AllWidgetData,
  newLineWidgetData: LineWidgetData,
  side: Side,
  newWidgetType: WidgetType,
) => {
  // This is the position of the line end that's connecting to the newWidgetData
  const lineEndX = newLineWidgetData.endPos.xPx + newLineWidgetData.leftPx;
  const lineEndY = newLineWidgetData.endPos.yPx + newLineWidgetData.topPx;
  let topPx = 0;
  let leftPx = 0;

  // Get new widget dimensions
  let newWidgetWidth = newWidgetData.widthPx;
  let newWidgetHeight = newWidgetData.heightPx;

  if (newWidgetType === WidgetType.Group) {
    const groupPosition = getGroupWidgetPositionFromWidgetIds((newWidgetData as GroupWidgetData).memberWidgetIds);
    newWidgetWidth = groupPosition.widthPx;
    newWidgetHeight = groupPosition.heightPx;
  }

  // To calculate new widget's position, first undo rotation to get the lineEndX position if it wasn't rotated.
  // to do that, find the center of the widget and rotate the lineEndX around that center.
  // then calculate the new position of the widget based on the unrotated lineEndX position.
  const centerOfWidgetPos = getRectCenterFromPointOnSide(
    newWidgetWidth,
    newWidgetHeight,
    { xPx: lineEndX, yPx: lineEndY },
    newWidgetData.rotateDeg,
    side,
  );
  const lineEndPosIfNotRotated = rotatePointAroundOrigin(
    { xPx: lineEndX, yPx: lineEndY },
    centerOfWidgetPos,
    -newWidgetData.rotateDeg,
  );

  // Now that's it's unrotated, can calculate the original top and left positions of the new widget
  if (side === Side.EAST) {
    topPx = lineEndPosIfNotRotated.yPx - newWidgetHeight / 2;
    leftPx = lineEndPosIfNotRotated.xPx - newWidgetWidth;
  } else if (side === Side.WEST) {
    topPx = lineEndPosIfNotRotated.yPx - newWidgetHeight / 2;
    leftPx = lineEndPosIfNotRotated.xPx;
  } else if (side === Side.NORTH) {
    topPx = lineEndPosIfNotRotated.yPx;
    leftPx = lineEndPosIfNotRotated.xPx - newWidgetWidth / 2;
  } else if (side === Side.SOUTH) {
    topPx = lineEndPosIfNotRotated.yPx - newWidgetHeight;
    leftPx = lineEndPosIfNotRotated.xPx - newWidgetWidth / 2;
  } else if (side === Side.CENTER) {
    topPx = lineEndPosIfNotRotated.yPx - newWidgetHeight / 2;
    leftPx = lineEndPosIfNotRotated.xPx - newWidgetWidth / 2;
  }

  return { topPx, leftPx };
};

/**
 * Determines the side that the endport is connected to based on the start and end
 * positions of the line widget.
 *
 * @param lineWidgetData line widget data
 * @returns Inferred side of the end port
 */
export const inferEndPortSide = (lineWidgetData: LineWidgetData): Side => {
  const { startPos, endPos } = lineWidgetData;
  const deltaX = endPos.xPx - startPos.xPx;
  const deltaY = endPos.yPx - startPos.yPx;

  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    return deltaX > 0 ? Side.WEST : Side.EAST;
  } else {
    return deltaY > 0 ? Side.NORTH : Side.SOUTH;
  }
};

// Duplicate a node widget (with a connected line)
export const duplicateNodeWidget = (widgetId: WidgetId, lineWidgetData: NewWidget) => {
  const widgets = selectWidgets(store.getState());
  const copiedWidget = clonedeep(widgets[widgetId]) as AllWidgetData;
  const newWidgetType = getWidgetTypeFromId(widgetId);

  const isResponsiveWidget = RESPONSIVE_WIDGET_LIST.includes(newWidgetType);
  const isGroupWidget = newWidgetType === WidgetType.Group;

  const newWidgetData = { widgetType: newWidgetType, widgetData: copiedWidget };
  const newLineWidgetData = clonedeep(lineWidgetData);

  const startPort = (newLineWidgetData.widgetData as LineWidgetData).startPort;
  if (!startPort) return;

  // If the end port has a center connection but the new widget type doesn't support it, infer the side
  let endPortSide = getOppositeSide(startPort.side);
  if (endPortSide === Side.CENTER && !ENABLED_CENTER_WIDGET_TYPES.includes(newWidgetType)) {
    endPortSide = inferEndPortSide(newLineWidgetData.widgetData as LineWidgetData);
  }

  const newPosition = calculateNewWidgetPos(
    newWidgetData.widgetData,
    newLineWidgetData.widgetData as LineWidgetData,
    endPortSide,
    newWidgetType,
  );

  if (newPosition && !isGroupWidget) {
    newWidgetData.widgetData.leftPx = newPosition.leftPx;
    newWidgetData.widgetData.topPx = newPosition.topPx;
  }

  // new widget should not be locked
  newWidgetData.widgetData.isLocked = false;

  // Create the new widget for the duplicated node widget
  let result: NewWidget | NewWidget[] = newWidgetData;
  if (isGroupWidget || isResponsiveWidget) {
    const groupMemberIds = (widgets[widgetId] as ResponsiveWidgetBaseData)?.memberWidgetIds;
    const groupWidgets = getClipboardGroupWidgets(groupMemberIds, widgetId, isResponsiveWidget, widgets);

    if (isGroupWidget) {
      // Go through each member widget and set the new position. Member widgets have their own
      // positioning relative to canvas, not group widget.
      const oldPosition = getGroupWidgetPositionFromWidgetIds(groupMemberIds);
      const deltaPosition = {
        topPx: newPosition.topPx - oldPosition.topPx,
        leftPx: newPosition.leftPx - oldPosition.leftPx,
      };
      groupWidgets.forEach((widget) => {
        widget.widgetData = {
          ...widget.widgetData,
          topPx: widget.widgetData.topPx + deltaPosition.topPx,
          leftPx: widget.widgetData.leftPx + deltaPosition.leftPx,
        };
      });
    }

    const newResponsiveWidgetData = {
      widgetType: getWidgetTypeFromId(widgetId),
      widgetData: copiedWidget,
      groupWidgets: groupWidgets,
      isResponsiveGroup: isResponsiveWidget,
    };
    result = parseCopyWidgets([{ ...newResponsiveWidgetData, fromPageId: '' }], 'not same page id', false); // hack, prevent shift in copy
  }

  // create a duplicated line widget - connect to the new node widget
  // use custom widget id (needed for the line widget to be connected to the new node widget)
  let connectLineToThisWidgetID = '';

  if (Array.isArray(result)) {
    // Array with group -> use group widget id
    // Array with responsive -> use responsive widget id
    const groupWidget = result.find((widget) => widget.widgetType === WidgetType.Group);
    if (groupWidget) {
      connectLineToThisWidgetID = generateWidgetId(WidgetType.Group);
      groupWidget.customWidgetId = connectLineToThisWidgetID;
    } else {
      const responsiveWidget = result.find((widget) => RESPONSIVE_WIDGET_LIST.includes(widget.widgetType));
      if (responsiveWidget) {
        connectLineToThisWidgetID = generateWidgetId(responsiveWidget.widgetType);
        responsiveWidget.customWidgetId = connectLineToThisWidgetID;
      }
    }
  } else {
    // Single widget -> use widget id
    connectLineToThisWidgetID = generateWidgetId(newWidgetType);
    result.customWidgetId = connectLineToThisWidgetID;
  }

  (newLineWidgetData.widgetData as LineWidgetData).endPort = {
    widgetId: connectLineToThisWidgetID,
    side: endPortSide,
    alignment: Alignment.CENTER,
  };

  // if the start port is center, then the line should appear below the start port widget in the layer order
  if (startPort.side === Side.CENTER || endPortSide === Side.CENTER) {
    newLineWidgetData.targetWidgetId = startPort.widgetId;
  }

  result = Array.isArray(result) ? [newLineWidgetData, ...result] : [newLineWidgetData, result];

  // finally add the duplicated widget and the new line widget
  store.dispatch(addNewWidget(result));

  // only set the new node widget as active
  // we need to reset the active widget first to prevent the line widget from being active
  store.dispatch(setActiveWidget(''));

  // timeout here so that it doesn't interfer with the line widget observer
  // For Safari 0 is not always enough so its increased a bit
  // TODO: recheck and update this after the diagram state has been updated
  const timeout = isBrowser.SAFARI ? 300 : 0;
  setTimeout(() => store.dispatch(setActiveWidget(connectLineToThisWidgetID)), timeout);
};

/**
 * Calculates new line widget data (topPx, leftPx, widthPx, heightPx, start/endPos) after changes to `widgetId`'s dimensions.
 *
 * @param widgetId
 * @param newWidgetDimension
 * @param lineWidgetData
 * @returns
 */
export const calculateNewLineWidgetPos = (
  widgetId: WidgetId,
  newWidgetDimension: WidgetPosition,
  lineWidgetData: LineWidgetData,
): {
  topPx: number;
  leftPx: number;
  widthPx: number;
  heightPx: number;
  startPos: Pos;
  endPos: Pos;
} | null => {
  const { startPort, endPort } = lineWidgetData;
  const isStart = startPort?.widgetId === widgetId;
  const isEnd = endPort?.widgetId === widgetId;

  // Widget isn't connected to the provided line
  if (!isStart && !isEnd) return null;

  const { rotateDeg } = newWidgetDimension;
  const side = isStart ? startPort.side : endPort!.side;

  // Compute the new position of the line end that is connected to this widget
  const { top: lineEndY, left: lineEndX } = generateWrapperStyle(newWidgetDimension, side, 1, 0, rotateDeg);
  const newLineEndXPx = parseFloat(lineEndX as string);
  const newLineEndYPx = parseFloat(lineEndY as string);
  // Calculate the position of the opposite line end (not connected to this widget)
  const oppositeLineEndX = isStart
    ? lineWidgetData.endPos.xPx + lineWidgetData.leftPx
    : lineWidgetData.startPos.xPx + lineWidgetData.leftPx;
  const oppositeLineEndY = isStart
    ? lineWidgetData.endPos.yPx + lineWidgetData.topPx
    : lineWidgetData.startPos.yPx + lineWidgetData.topPx;

  // Compute new top and left px
  const newTopPx = Math.min(newLineEndYPx, oppositeLineEndY);
  const newLeftPx = Math.min(newLineEndXPx, oppositeLineEndX);

  // Compute new start and end positions determined by the new line end positions and connected port
  let newStartPos = { xPx: 0, yPx: 0 };
  let newEndPos = { xPx: 0, yPx: 0 };
  if (isStart) {
    newStartPos = {
      xPx: newLineEndXPx - newLeftPx,
      yPx: newLineEndYPx - newTopPx,
    };
    newEndPos = {
      xPx: oppositeLineEndX - newLeftPx,
      yPx: oppositeLineEndY - newTopPx,
    };
  } else if (isEnd) {
    newStartPos = {
      xPx: oppositeLineEndX - newLeftPx,
      yPx: oppositeLineEndY - newTopPx,
    };
    newEndPos = {
      xPx: newLineEndXPx - newLeftPx,
      yPx: newLineEndYPx - newTopPx,
    };
  }

  const newWidthPx = Math.max(newLineEndXPx, oppositeLineEndX) - newLeftPx;
  const newHeightPx = Math.max(newLineEndYPx, oppositeLineEndY) - newTopPx;

  const result = {
    topPx: newTopPx,
    leftPx: newLeftPx,
    widthPx: newWidthPx,
    heightPx: newHeightPx,
    startPos: newStartPos,
    endPos: newEndPos,
  };

  return result;
};
